import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Button,
  Box,
  Pagination,
  Checkbox,
  Backdrop,
  CircularProgress
} from '@mui/material';
import OurLayouts from '../Layouts';
import apiService from '../../services/apiservice';
import { FaFilter } from 'react-icons/fa';
import { Sidebar } from "primereact/sidebar";
import moment from 'moment';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import PatientSearch from '../Shared/patientSearch';
import { Dropdown } from "primereact/dropdown";
import { toast } from 'react-toastify';

const STATUS_OPTIONS = [
  { id: 4, value: 'Approved' },
  { id: 5, value: 'Rejected' },
];


const BILLING_COLORS = {
  1: '#ECAC09',
  2: 'violet',
  3: '#7225EF',
  4: '#299F5D',
  5: '#DD0000'
};

const BILLING_STATUS_MAP = {
  1: 'Billing Uploaded',
  2: 'Billing Done',
  3: 'Billing Completed',
  4: 'Billing Approved',
  5: 'Billing Rejected'
};

const ITEMS_PER_PAGE = 10;

const PracticeAdminBillList = () => {
  const userObj = JSON.parse(localStorage.getItem('loginObj') || '{}');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [billingList, setBillingList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState({});
  const [viewEnquiry, setViewEnquiry] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [patientSearchSidebar, setPatientSearchSidebar] = useState(false);

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    const [month, day, year] = dateString.split("/");
    return new Date(year, month - 1, day);
  };

  const getBillingStyle = useCallback((billingStatus) => ({
    color: BILLING_COLORS[billingStatus] || '#3D3BA4',
  }), []);

  const handleChangeTab = useCallback((tab) => {
    setFilterObj({});
    setActiveTab(tab);
    setCurrentPage(1);
    setSelected({});
  }, []);

  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value);
  }, []);

  const toggleSidebarFilter = useCallback(() => {
    setPatientSearchSidebar(prev => !prev);
  }, []);

  const handleSelectedData = useCallback((e) => {
    setFilterObj(prev => ({
      ...prev,
      patientId: e.code,
      patientName: e.name,
      nameSearch: ""
    }));
  }, []);

  const getBillingList = useCallback(async () => {
    const res = await apiService.userInfoAll(userObj.user?.code);
    const payload = {};

    try {
      payload.billingStatus = 3;
      payload.pageNumber = currentPage;
      payload.hospitalId = res?.data?.successList
        ?.map(item => item.hospitalId)
        .join(',');
      setLoading(true);

      if (activeTab === 1) {
        if (filterObj.billingStatus) {
          payload.billingStatus = filterObj.billingStatus;
        }
        if (!filterObj.billingStatus) {
          payload.roleCode = "PRADMIN";
          delete payload.billingStatus;
        }
      }

      if (filterObj.id) {
        payload.id = filterObj.id;
      }
      if (filterObj.patientId) {
        payload.patientId = filterObj.patientId;
      }
      if (filterObj.fromDate) {
        payload.fromDate = filterObj.fromDate;
      }
      if (filterObj.toDate) {
        payload.toDate = filterObj.toDate;
      }
      if (filterObj.nameSearch) {
        payload.nameSearch = filterObj.nameSearch;
      }

      const result = await apiService.getServicesBillList(payload);
      if (result.data.responseCode === '200') {
        setLoading(false);
        setBillingList(result.data.successList);
      }
    } catch (error) {
      console.error('Error fetching billing list:', error);
      setLoading(false);
    }
  }, [userObj.user?.code, currentPage, activeTab, filterObj]);

  const handleAction = useCallback(async (data, tab) => {
    const payload = [{
      modifiedBy: userObj.user?.code,
      modifiedDate: moment(new Date()).format("YYYY-MM-DD HH:mm:ss"),
      billingStatus: tab === "A" ? 4 : 5,
      id: data.id,
      serviceEnquiryId: data.serviceEnquiryId,
      billingPublishStatus: tab
    }];

    const result = await apiService.billingApprovalUpdate(payload);
    if (result) {
      const message = tab === "A" ? "Approved successfully" : "Rejected successfully";
      toast(message);
      getBillingList();
    }
  }, [userObj.user?.code, getBillingList]);

  const handleCheckboxChange = useCallback((id) => {
    setSelected(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const handleSelectAll = useCallback((event) => {
    const isChecked = event.target.checked;
    const newSelected = {};
    billingList.forEach((item) => {
      newSelected[item.id] = isChecked;
    });
    setSelected(newSelected);
  }, [billingList]);

  const handleActionAll = useCallback(async (tab) => {
    const selectedRows = billingList.filter(item => selected[item.id]);
    if (selectedRows.length > 0) {
      const payload = selectedRows.map(item => ({
        modifiedBy: userObj.user?.code,
        modifiedDate: moment(new Date()).format("YYYY-MM-DD HH:mm:ss"),
        billingStatus: tab === "A" ? 4 : 5,
        id: item.id,
        serviceEnquiryId: item.serviceEnquiryId,
        billingPublishStatus: tab
      }));
      const result = await apiService.billingApprovalUpdate(payload);
      if (result) {
        const message = tab === "A" ? "Approved successfully" : "Rejected successfully";
        toast(message);
        getBillingList();
      }
    } else {
      toast("Nothing selected");
    }
  }, [billingList, selected, userObj.user?.code, getBillingList]);

  const handleClickFilter = useCallback(() => {
    setViewEnquiry(true);
  }, []);

  const handleChangeFilter = useCallback((e) => {
    const { name, value } = e.target;

    if (name === "fromDate" || name === "toDate") {
      setFilterObj(prev => ({
        ...prev,
        [name]: formatDate(value),
      }));
    } else if (name === "nameSearch" && value) {
      setFilterObj(prev => ({
        ...prev,
        [name]: value,
        patientId: "",
        patientName: ""
      }));
    } else {
      setFilterObj(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  }, []);

  const filterService = useCallback(() => {
    setViewEnquiry(false);
    setCurrentPage(1);
    getBillingList();
  }, [getBillingList]);

  const handleCancel = useCallback(() => {
    setFilterObj({});
  }, []);

  useEffect(() => {
    getBillingList();
  }, [activeTab, currentPage, getBillingList]);

  const selectedCount = useMemo(() =>
    Object.keys(selected).filter(key => selected[key]).length,
    [selected]
  );

  const isAllSelected = useMemo(() =>
    billingList.length > 0 && billingList.every(item => selected[item.id]),
    [billingList, selected]
  );

  const totalPages = useMemo(() =>
    Math.ceil(billingList[0]?.pagination?.totalRecords / ITEMS_PER_PAGE) || 0,
    [billingList]
  );

  const sidebarFooter = (
    <div className="grid mt-36 ml-3 grid-cols-2">
      <div>
        <button
          className="h-9 bg-[#004172] text-white rounded-md w-full"
          onClick={filterService}
        >
          Ok
        </button>
      </div>
      <div>
        <button
          className="ml-3 h-9 border border-[#004172] text-[#004172] font-bold font-monospace rounded-md w-full"
          onClick={handleCancel}
        >
          Clear
        </button>
      </div>
    </div>
  );

  const renderFilterSidebar = (
    <Sidebar
      style={{ width: '45%', borderBottom: '1px solid #ccc' }}
      dismissable={false}
      visible={viewEnquiry}
      header={"Filter Service"}
      centered={true}
      onHide={() => setViewEnquiry(false)}
      position="right"
    >
      <div className="grid mt-4 grid-cols-2 gap-4">
        <div>
          <label htmlFor="dob">From Date</label>
          <Calendar
            id="date"
            className="border border-gray-300 rounded-md p-2 w-full"
            dateFormat="dd/mm/yy"
            style={{ height: '40px' }}
            onChange={handleChangeFilter}
            name='fromDate'
            value={parseDate(filterObj.fromDate)}
            placeholder="Select From Date"
          />
        </div>
        <div>
          <label htmlFor="dob">To Date</label>
          <Calendar
            id="date"
            className="border border-gray-300 rounded-md p-2 w-full"
            dateFormat="dd/mm/yy"
            style={{ height: '40px' }}
            onChange={handleChangeFilter}
            name='toDate'
            value={parseDate(filterObj.toDate)}
            placeholder="Select To Date"
          />
        </div>
        <div>
          <label htmlFor="nameSearch">Patient Name</label>
          <div className="relative">
            <input
              id="nameSearch"
              name="nameSearch"
              className="border border-gray-300 rounded-md p-2 pr-10 w-full"
              onChange={handleChangeFilter}
              value={filterObj.nameSearch || ''}
              placeholder="Search by patient name"
              type="text"
            />
          </div>
        </div>
        <div>
          <label htmlFor="emailId">Select Patient</label>
          <div className="relative">
            <InputText
              id="patientName"
              className="border border-gray-300 rounded-md p-2 pr-10 w-full"
              value={filterObj.patientName || ''}
              name="patientName"
              placeholder="Click icon to search patients"
              readOnly
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-2"
              onClick={toggleSidebarFilter}
              aria-label="Toggle sidebar filter"
            >
              <svg
                className="w-5 h-5 text-gray-400"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M21 21l-5.2-5.2"></path>
                <circle cx="10" cy="10" r="7"></circle>
              </svg>
            </button>

          </div>
        </div>
        <div>
          <label htmlFor="id">Enquiry Id</label>
          <div className="relative">
            <InputText
              id="id"
              name="id"
              className="border border-gray-300 rounded-md p-2 pr-10 w-full"
              onChange={handleChangeFilter}
              value={filterObj.id || ''}
              placeholder="Search for Enquiry Id"
            />
          </div>
        </div>
        {activeTab === 1 && (
          <div>
            <label htmlFor="status">Status</label>
            <Dropdown
              value={filterObj.billingStatus || ''}
              options={STATUS_OPTIONS}
              optionLabel="value"
              placeholder="Select Status"
              optionValue='id'
              name='billingStatus'
              className="border border-gray-300 rounded-md w-full"
              onChange={handleChangeFilter}
              filter
            />
          </div>
        )}
      </div>
      {sidebarFooter}
    </Sidebar>
  );

  const renderTableHeader = (
    <TableHead>
      <TableRow>
        {activeTab === 0 && (
          <TableCell style={{ fontSize: '17px', fontWeight: 600 }}>
            <div style={{ width: 'max-content' }}>
              Select All <Checkbox
                checked={isAllSelected}
                onChange={handleSelectAll}
              />
            </div>
          </TableCell>
        )}
        <TableCell style={{ fontSize: '15px', fontWeight: 600 }}>Enquiry ID</TableCell>
        <TableCell style={{ fontSize: '15px', fontWeight: 600 }}>Patient ID</TableCell>
        <TableCell style={{ fontSize: '15px', fontWeight: 600 }}>Patient Name</TableCell>
        <TableCell style={{ fontSize: '15px', fontWeight: 600 }}>Procedure Name</TableCell>
        <TableCell style={{ fontSize: '15px', fontWeight: 600 }}>eClinic Name</TableCell>
        <TableCell style={{ fontSize: '15px', fontWeight: 600 }}>Preffered Hospital & Clinic</TableCell>
        <TableCell style={{ fontSize: '15px', fontWeight: 600 }}>DoctorName</TableCell>
        <TableCell style={{ fontSize: '15px', fontWeight: 600 }}>Gross Amount</TableCell>
        <TableCell style={{ fontSize: '15px', fontWeight: 600 }}>MCI</TableCell>
        <TableCell style={{ fontSize: '15px', fontWeight: 600 }}>Net Sale</TableCell>
        <TableCell style={{ fontSize: '15px', fontWeight: 600 }}>Total Amount</TableCell>
        <TableCell style={{ fontSize: '17px', fontWeight: 600, width: '200px' }}>Status</TableCell>
        {activeTab === 0 && (
          <TableCell style={{ fontSize: '17px', fontWeight: 600, width: '200px' }}>Action</TableCell>
        )}
      </TableRow>
    </TableHead>
  );

  const renderTableBody = billingList.map((item) => (
    <TableRow key={item.id}>
      {activeTab === 0 && (
        <TableCell style={{ fontSize: '17px', fontWeight: 600 }}>
          <Checkbox
            checked={selected[item.id] || false}
            onChange={() => handleCheckboxChange(item.id)}
          />
        </TableCell>
      )}
      <TableCell style={{ fontSize: '13px', fontWeight: 500 }}>
        {item.serviceEnquiryId}
      </TableCell>
      <TableCell style={{ fontSize: '13px', fontWeight: 500 }}>
        {item.patientId}
      </TableCell>
      <TableCell style={{ fontSize: '13px', fontWeight: 500 }}>
        {item.patientName}
      </TableCell>
      <TableCell style={{ fontSize: '13px', fontWeight: 500 }}>
        {item.procedureName}
      </TableCell>
      <TableCell style={{ fontSize: '13px', fontWeight: 500 }}>
        {item.cheCenterName}
      </TableCell>
      <TableCell style={{ fontSize: '13px', fontWeight: 500 }}>
        {item.hospitalName}
      </TableCell>
      <TableCell style={{ fontSize: '13px', fontWeight: 500 }}>
        {item.referralUserName}
      </TableCell>
      <TableCell style={{ fontSize: '13px', fontWeight: 500 }}>
        {item.gross}
      </TableCell>
      <TableCell style={{ fontSize: '13px', fontWeight: 500 }}>
        {item.mci}
      </TableCell>
      <TableCell style={{ fontSize: '13px', fontWeight: 500 }}>
        {item.yieldAmount}
      </TableCell>
      <TableCell style={{ fontSize: '13px', fontWeight: 500 }}>
        {item.totalAmount}
      </TableCell>
      <TableCell style={{ fontSize: '15px', fontWeight: 500 }}>
        <span style={getBillingStyle(item.billingStatus)}>
          {BILLING_STATUS_MAP[item.billingStatus] || ''}
        </span>
      </TableCell>
      {activeTab === 0 && (
        <TableCell style={{ fontSize: '12px', fontWeight: 500 }}>
          <div>
            <button
              className="h-9 w-28 bg-[#004172] text-white rounded-md"
              onClick={() => handleAction(item, "A")}
            >
              Approve
            </button>
            <button
              className="mt-1 w-28 h-9 border border-[#004172] bg-[#fff] text-black rounded-md"
              onClick={() => handleAction(item, "R")}
            >
              Reject
            </button>
          </div>
        </TableCell>
      )}
    </TableRow>
  ));

  return (
    <>
      <OurLayouts>
        <Backdrop
          sx={{ color: "#FFFFFF", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress size={80} color="inherit" />
        </Backdrop>

        {renderFilterSidebar}

        <div className="flex flex-col w-full mt-[7rem] overflow-auto">
          <div className="flex justify-between mt-[3rem]">
            <div>
              <h1 className="text-2xl text-primary font-semibold">Billing Approval</h1>
            </div>
            <div className='flex border h-10 rounded-md pt-1 pb-1 mr-12 justify-items-center'>
              <div className='flex mx-2'>
                <Button
                  className='flex gap-1'
                  onClick={handleClickFilter}
                >
                  <FaFilter className='text-primary' />
                  <h6 className='text-primary'>Filter</h6>
                </Button>
              </div>
            </div>
          </div>

          {selectedCount > 0 && (
            <div className="flex flex-end justify-end mt-4 mr-4">
              <div>
                <button
                  className="h-9 p-2 bg-[#004172] text-white rounded-md"
                  onClick={() => handleActionAll("A")}
                >
                  Approve Selected
                </button>
              </div>
              <div>
                <button
                  className="ml-2 p-2 h-9 border border-[#004172] bg-[#fff] text-black rounded-md"
                  onClick={() => handleActionAll("R")}
                >
                  Reject Selected
                </button>
              </div>
            </div>
          )}

          <div
            className="w-[99%] mt-8 rounded-[5px]"
            style={{ boxShadow: "0px 8px 25px 0px rgba(0, 0, 0, 0.06)" }}
          >
            <Tabs
              value={activeTab}
              onChange={(event, newValue) => handleChangeTab(newValue)}
            >
              <Tab label={<span style={{ fontWeight: 'bold' }}>Assign To Me</span>} />
              <Tab label={<span style={{ fontWeight: 'bold' }}>ALL</span>} />
            </Tabs>

            <TableContainer
              component={Paper}
              className="print-table"
              style={{ width: '100%', overflowX: 'auto' }}
            >
              <Table>
                {renderTableHeader}
                <TableBody>
                  {renderTableBody}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                color="secondary"
                onChange={handlePageChange}
              />
            </Box>
          </div>
        </div>
      </OurLayouts>

      <PatientSearch
        showbar={patientSearchSidebar}
        setshowBar={toggleSidebarFilter}
        onSelect={handleSelectedData}
      />
    </>
  );
};

export default PracticeAdminBillList;